const STORAGE_KEY = 'hiromatch:support-error-buffer';
const MAX_ENTRIES = 20;

export interface SupportErrorEntry {
  ts: number;
  message: string;
  stack?: string;
  source?: string;
}

export interface SupportDiagnosticsBundle {
  pathname: string;
  search: string;
  href: string;
  userAgent: string;
  language: string;
  timezone: string;
  release: string;
  selectedCompanyId?: string;
  accountType?: string;
  planId?: string;
  sentryEventId?: string;
  recentErrors: SupportErrorEntry[];
}

let captureInstalled = false;

function readBuffer(): SupportErrorEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as SupportErrorEntry[]) : [];
  } catch {
    return [];
  }
}

function writeBuffer(entries: SupportErrorEntry[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // sessionStorage full or disabled — ignore
  }
}

export function recordSupportError(entry: Omit<SupportErrorEntry, 'ts'> & { ts?: number }): void {
  const next: SupportErrorEntry = {
    ts: entry.ts ?? Date.now(),
    message: entry.message.slice(0, 2000),
    ...(entry.stack ? { stack: entry.stack.slice(0, 8000) } : {}),
    ...(entry.source ? { source: entry.source.slice(0, 200) } : {}),
  };
  writeBuffer([...readBuffer(), next]);
}

const SENSITIVE_QUERY_KEYS = ['token', 'access_token', 'id_token', 'oobCode', 'apiKey'];

function sanitizeHref(href: string): string {
  try {
    const u = new URL(href);
    for (const key of [...u.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.some((k) => key.toLowerCase().includes(k.toLowerCase()))) {
        u.searchParams.set(key, '[redacted]');
      }
    }
    return u.toString();
  } catch {
    return href.slice(0, 2000);
  }
}

/**
 * Registers window error handlers once per tab session.
 * Call from AppLayout when the recruiter shell mounts.
 */
export function initSupportDiagnosticsCapture(): void {
  if (captureInstalled || typeof window === 'undefined') return;
  captureInstalled = true;

  window.addEventListener('error', (event) => {
    recordSupportError({
      message: event.message || String(event.error ?? 'Unknown error'),
      stack: event.error instanceof Error ? event.error.stack : undefined,
      source: event.filename ? `${event.filename}:${event.lineno}` : 'window.error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
    recordSupportError({
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
      source: 'unhandledrejection',
    });
  });
}

export function buildDiagnosticsBundle(context: {
  selectedCompanyId?: string;
  accountType?: string;
  planId?: string;
  sentryEventId?: string;
}): SupportDiagnosticsBundle {
  const { pathname, search } = window.location;
  return {
    pathname,
    search,
    href: sanitizeHref(window.location.href),
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    release: (import.meta.env.VITE_RELEASE_TAG as string | undefined) ?? 'unknown',
    ...(context.selectedCompanyId ? { selectedCompanyId: context.selectedCompanyId } : {}),
    ...(context.accountType ? { accountType: context.accountType } : {}),
    ...(context.planId ? { planId: context.planId } : {}),
    ...(context.sentryEventId ? { sentryEventId: context.sentryEventId } : {}),
    recentErrors: readBuffer(),
  };
}

export function getRecentErrorCount(): number {
  return readBuffer().length;
}
